import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Badge from '@/modules/common/components/Badge.tsx';
import OverdueTaskBadge from '@/modules/common/components/OverdueTaskBadge.tsx';
import TaskDates from '@/modules/common/components/TaskDates.tsx';
import { checkIfTaskOverdueSoon } from '@/modules/common/helpers/checkIfTaskOverdueSoon.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import UsersAvatarsSet from '@/modules/admin-dashboard/components/UsersAvatarsSet.tsx';
import { priorityColors } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import TaskItemDetailedInfo from '@/modules/employee-dashboard/components/items/TaskItemDetailedInfo.tsx';
import { ChevronDownIcon } from 'ui/icons';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
  TaskResult,
} from 'compliance-api';

interface TaskItemProps {
  isUpcoming?: boolean;
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
}

const TaskItem = ({ task, isUpcoming }: TaskItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isTaskOverdueSoon = checkIfTaskOverdueSoon(task);
  const overdueResultsCount = useMemo(
    () => (task?.task_results || []).filter(({ result }) => result === TaskResult.Due).length,
    [task?.task_results],
  );

  const handleToggleOpen = useCallback(() => {
    setIsOpen(open => !open);
  }, []);
  return (
    <div
      className={classNames(
        'sm:border sm:rounded-xl border-gray-200 [&:not(:last-child)]:border-b',
      )}
    >
      <div
        className="pb-2 pl-2 sm:pb-4 sm:px-6 sm:pt-4 relative w-full grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 cursor-pointer overflow-hidden"
        onClick={handleToggleOpen}
      >
        <div
          className={classNames(
            'absolute left-0 top-0 w-[11px] h-full sm:rounded-tl-xl',
            { 'sm:rounded-bl-xl': !isOpen },
            priorityColors[task.priority],
          )}
          style={{ clipPath: 'inset(0px 6px 0px 0px)' }}
        />
        <img
          src={ChevronDownIcon}
          alt="open"
          width={24}
          className={classNames('select-none hover:opacity-80 min-w-6', { '-rotate-90': !isOpen })}
        />
        <div className="space-y-1.5 overflow-hidden">
          {isUpcoming && <span className="text-sm h-fit">{task.name}</span>}
          {!isUpcoming && (
            <Link
              className="text-sm hover:underline h-fit"
              to={{ pathname: commonPaths.dynamic.task(task.id), search: location.search }}
            >
              {task.name}
            </Link>
          )}
          <div className="flex gap-1.5 text-nowrap">
            {!!overdueResultsCount && <OverdueTaskBadge count={overdueResultsCount} />}
            {isTaskOverdueSoon && (
              <Badge type="warning" className="block">
                Overdue soon
              </Badge>
            )}
          </div>
        </div>
        <TaskDates
          current_start_date={task.current_start_date}
          overdue_date={task.overdue_date}
          classes={{ container: '!w-[200px]' }}
        />
        {task.users && (
          <div className="w-[100px] hidden lg:block">
            <UsersAvatarsSet users={task.users} className="[&>div:first-child]:!justify-start" />
          </div>
        )}
      </div>
      {isOpen && <TaskItemDetailedInfo task={task} />}
    </div>
  );
};

export default TaskItem;
